import { useCallback, useEffect, useState } from 'react'
import { API_BASE_URL } from '../utils/apiBaseUrl'
import { apiFetch } from '../utils/apiFetch'
import {
  DEFAULT_FILTER_PRESET,
  normalizeFilterPreset,
  type ControlCenterFilterPreset,
} from '../utils/controlCenterFilterPreset'

let cachedPreset: ControlCenterFilterPreset | null = null
let inflight: Promise<ControlCenterFilterPreset> | null = null

async function fetchFilterPreset(): Promise<ControlCenterFilterPreset> {
  if (cachedPreset) return cachedPreset
  if (inflight) return inflight
  inflight = (async () => {
    try {
      const token = localStorage.getItem('lts_jwt_token')
      const res = await apiFetch(`${API_BASE_URL}/control-center/filter-preset`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      const payload = await res.json().catch(() => ({}))
      if (!res.ok || !payload?.success) {
        const fallback = normalizeFilterPreset(DEFAULT_FILTER_PRESET)
        cachedPreset = fallback
        return fallback
      }
      const next = normalizeFilterPreset({ ...DEFAULT_FILTER_PRESET, ...payload.data })
      cachedPreset = next
      return next
    } catch {
      const fallback = normalizeFilterPreset(DEFAULT_FILTER_PRESET)
      cachedPreset = fallback
      return fallback
    } finally {
      inflight = null
    }
  })()
  return inflight
}

export function invalidateFilterPresetCache() {
  cachedPreset = null
}

export function useControlCenterFilterPreset() {
  const [config, setConfig] = useState<ControlCenterFilterPreset>(
    () => cachedPreset ?? DEFAULT_FILTER_PRESET,
  )
  const [loading, setLoading] = useState(!cachedPreset)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    invalidateFilterPresetCache()
    const next = await fetchFilterPreset()
    setConfig(next)
    setLoading(false)
    return next
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const next = await fetchFilterPreset()
      if (!cancelled) {
        setConfig(next)
        setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const save = useCallback(async (partial: Partial<ControlCenterFilterPreset>) => {
    const token = localStorage.getItem('lts_jwt_token')
    const res = await apiFetch(`${API_BASE_URL}/control-center/filter-preset`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(partial),
    })
    const payload = await res.json().catch(() => ({}))
    if (!res.ok || !payload?.success) {
      const message = payload?.message || 'Failed to save filter preset'
      setError(message)
      throw new Error(message)
    }
    const next = normalizeFilterPreset({ ...DEFAULT_FILTER_PRESET, ...payload.data })
    cachedPreset = next
    setConfig(next)
    setError(null)
    return next
  }, [])

  return { config, loading, error, refresh, save }
}
